// RetentionAnalysis — strategic dashboard: why members leave and when.
// Sections: retention gauge (multi-period), cohort survival curves, attrition cause breakdown,
// attrition timing histogram, at-risk members, status transition summary, separation DataTable,
// member voice survey insights (Dec 2024), monthly attrition ledger (2024).

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { useAggregates } from "../hooks/useDashboard";
import {
  useMembershipChanges,
  useRetentionCohorts,
  useAtRisk,
  useBenchmarks,
} from "../hooks/useForge";
import type {
  MembershipChangeRow,
  AtRiskRow,
  RetentionCohortRow,
} from "../hooks/useForge";
import BenchmarkIndicator from "../components/dashboard/BenchmarkIndicator";
import { useRetentionThemes, useAttritionMonthly } from "../hooks/useStatic";
import type {
  RetentionThemeRow,
  AttritionMonthlyRow,
} from "../hooks/useStatic";
import { Link } from "react-router-dom";
import GaugeChart from "../components/dashboard/GaugeChart";
import CohortChart from "../components/dashboard/CohortChart";
import type { CohortSeries } from "../components/dashboard/CohortChart";
// AlertList imported for type reference only
// import AlertList from '../components/dashboard/AlertList';
import type { AlertItem } from "../components/dashboard/AlertList";
import ChartCard from "../components/dashboard/ChartCard";
import KPICard from "../components/dashboard/KPICard";
import StatusBadge from "../components/dashboard/StatusBadge";
import { useTheme } from "../hooks/useTheme";
import { getChartTheme } from "../lib/chart-theme";
import RiskMonitor from "../components/ai/RiskMonitor";

// ── Constants ─────────────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#2a7a6c";
const TOP_CAUSE_COLOR = "#dc2626";

/** Statuses considered "active" for survival curve computation */
const ACTIVE_STATUSES = new Set([
  "Running",
  "Probationary",
  "Associate (Active)",
  "Life Member (Active)",
  "Probationary (Cadet)",
  "Probationary (HS)",
  "Associate (Probationary)",
]);

/** Statuses considered "negative" transitions (active → away) */
const NEGATIVE_NEW_STATUSES = new Set([
  "Inactive",
  "Dropped",
  "Resigned",
  "Resigned - Rehire",
  "Transferred",
  "Deceased",
  "Administrative",
]);

const SURVIVAL_MILESTONES = [0, 3, 6, 12, 24, 36, 60];

const TIMING_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "0–3 mo", min: 0, max: 3 },
  { label: "3–6 mo", min: 3, max: 6 },
  { label: "6–12 mo", min: 6, max: 12 },
  { label: "12–24 mo", min: 12, max: 24 },
  { label: "24–36 mo", min: 24, max: 36 },
  { label: "36–60 mo", min: 36, max: 60 },
  { label: "60+ mo", min: 60, max: Infinity },
];

// ── Date range ────────────────────────────────────────────────────────────────

type DateRange = "ytd" | "l12m" | "l3y" | "l5y" | "all";

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  ytd: "YTD",
  l12m: "Last 12 Mo",
  l3y: "Last 3 Yr",
  l5y: "Last 5 Yr",
  all: "All Time",
};

function getDateRangeCutoff(range: DateRange): string | null {
  const now = new Date();
  switch (range) {
    case "ytd":
      return `${now.getFullYear()}-01-01`;
    case "l12m": {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return d.toISOString().slice(0, 10);
    }
    case "l3y": {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 3);
      return d.toISOString().slice(0, 10);
    }
    case "l5y": {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 5);
      return d.toISOString().slice(0, 10);
    }
    case "all":
    default:
      return null;
  }
}

function DateRangeToggle({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  const ranges: DateRange[] = ["ytd", "l12m", "l3y", "l5y", "all"];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {ranges.map((r) => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[32px] ${
            value === r
              ? "bg-dcvfd-accent text-white"
              : "bg-theme-overlay text-theme-secondary hover:bg-theme-overlay/80"
          }`}
        >
          {DATE_RANGE_LABELS[r]}
        </button>
      ))}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Returns months between two dates, rounded down.
 * Returns null if either date is invalid.
 */
function monthsBetween(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  if (!from || !to) return null;
  const a = new Date(from);
  const b = new Date(to);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  return Math.max(
    0,
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()),
  );
}

// ── Retention rate computation ─────────────────────────────────────────────────

interface RetentionPeriod {
  label: string;
  years: number;
  rate: number | null;
  cohortSize: number;
}

/**
 * Compute retention rate for a cohort window.
 * For an N-year retention rate: look at members who joined between (now - 2N) and (now - N)
 * and check how many are still active today.
 */
function computeRetentionRate(
  rows: RetentionCohortRow[],
  years: number,
): { rate: number | null; cohortSize: number } {
  const now = new Date();
  const windowEnd = new Date(now);
  windowEnd.setFullYear(windowEnd.getFullYear() - years);
  const windowStart = new Date(now);
  windowStart.setFullYear(windowStart.getFullYear() - years * 2);

  const cohort = rows.filter((r) => {
    if (!r.joined_date) return false;
    const d = new Date(r.joined_date);
    if (isNaN(d.getTime())) return false;
    return d >= windowStart && d <= windowEnd;
  });

  if (cohort.length === 0) return { rate: null, cohortSize: 0 };

  const stillActive = cohort.filter((m) => {
    if (!m.separation_date) {
      return ACTIVE_STATUSES.has(m.status_new ?? "");
    }
    const sepDate = new Date(m.separation_date);
    return !isNaN(sepDate.getTime()) && sepDate > now;
  });

  return {
    rate: stillActive.length / cohort.length,
    cohortSize: cohort.length,
  };
}

/**
 * Compute YTD retention: members who joined this calendar year and are still active.
 */
function computeYtdRetention(rows: RetentionCohortRow[]): {
  rate: number | null;
  cohortSize: number;
} {
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);

  const cohort = rows.filter((r) => {
    if (!r.joined_date) return false;
    const d = new Date(r.joined_date);
    return !isNaN(d.getTime()) && d >= yearStart && d <= now;
  });

  if (cohort.length === 0) return { rate: null, cohortSize: 0 };

  const stillActive = cohort.filter((m) => {
    if (!m.separation_date) {
      return ACTIVE_STATUSES.has(m.status_new ?? "");
    }
    const sepDate = new Date(m.separation_date);
    return !isNaN(sepDate.getTime()) && sepDate > now;
  });

  return {
    rate: stillActive.length / cohort.length,
    cohortSize: cohort.length,
  };
}

// ── Cohort survival computation ───────────────────────────────────────────────

function computeCohortSeries(rows: RetentionCohortRow[]): CohortSeries[] {
  const currentYear = new Date().getFullYear();
  const START_YEAR = 2018;

  // Group members by join year
  const byYear = new Map<number, RetentionCohortRow[]>();
  for (let y = START_YEAR; y <= currentYear; y++) {
    byYear.set(y, []);
  }

  for (const row of rows) {
    if (!row.joined_date) continue;
    const d = new Date(row.joined_date);
    if (isNaN(d.getTime())) continue;
    const year = d.getFullYear();
    if (year < START_YEAR || year > currentYear) continue;
    byYear.get(year)?.push(row);
  }

  const series: CohortSeries[] = [];

  for (const [year, members] of byYear.entries()) {
    if (members.length === 0) continue;

    // Only include cohorts where the oldest milestone is reachable
    const cohortAge = (currentYear - year) * 12;
    if (cohortAge < 3) continue;

    const dataPoints = SURVIVAL_MILESTONES.map((months) => {
      if (months > cohortAge) return null;

      const stillActive = members.filter((m) => {
        if (!m.separation_date) {
          return ACTIVE_STATUSES.has(m.status_new ?? "");
        }
        const monthsToSep = monthsBetween(m.joined_date, m.separation_date);
        if (monthsToSep === null) return true;
        return monthsToSep > months;
      });

      return {
        months,
        pctActive:
          members.length > 0 ? (stillActive.length / members.length) * 100 : 0,
      };
    }).filter((dp): dp is { months: number; pctActive: number } => dp !== null);

    if (dataPoints.length > 0) {
      // Right-censored: cohort hasn't yet had ~12 months to be observed, so its
      // later milestones overstate retention (e.g. 2025/2026 join years).
      const censored = cohortAge < 12;
      series.push({ year, dataPoints, censored });
    }
  }

  return series.sort((a, b) => a.year - b.year);
}

// ── Attrition cause breakdown ─────────────────────────────────────────────────

interface CauseRow {
  cause: string;
  count: number;
  isTop: boolean;
}

function computeAttritionCauses(rows: MembershipChangeRow[]): CauseRow[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const cause = asString(row.attrition_cause);
    if (!cause) continue;
    counts.set(cause, (counts.get(cause) ?? 0) + 1);
  }
  const sorted = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([cause, count], idx) => ({ cause, count, isTop: idx === 0 }));
  return sorted;
}

// ── Attrition timing histogram ────────────────────────────────────────────────

interface TimingBucket {
  label: string;
  count: number;
}

function computeAttritionTiming(rows: MembershipChangeRow[]): TimingBucket[] {
  const bucketCounts = TIMING_BUCKETS.map(() => 0);

  for (const row of rows) {
    const newStatus = asString(row.new_status);
    if (!NEGATIVE_NEW_STATUSES.has(newStatus)) continue;

    let months: number | null = null;

    // Prefer stored los_active_months
    const storedLos = row.los_active_months;
    if (storedLos !== null && storedLos !== undefined) {
      const n = asNumber(storedLos, -1);
      if (n >= 0) months = n;
    }

    // Fallback: compute from separation date and join date if available
    if (months === null) {
      const effDate = asString(row.effective_date);
      const createdAt = asString(row.airtable_created_time);
      // Use current_status_in_members date fields if available
      const joinDateRaw = asString(
        (row as Record<string, unknown>)["join_date"] ??
          (row as Record<string, unknown>)["joined_date"] ??
          "",
      );
      const from = joinDateRaw || createdAt;
      const to = effDate;
      if (from && to) {
        const computed = monthsBetween(from, to);
        if (computed !== null && computed >= 0) months = computed;
      }
    }

    if (months === null) continue;

    const bucketIdx = TIMING_BUCKETS.findIndex(
      (b) => months! >= b.min && months! < b.max,
    );
    if (bucketIdx >= 0) bucketCounts[bucketIdx]++;
  }

  return TIMING_BUCKETS.map((b, i) => ({
    label: b.label,
    count: bucketCounts[i],
  }));
}

// ── Status transition summary ─────────────────────────────────────────────────

interface TransitionRow {
  from: string;
  to: string;
  count: number;
  isNegative: boolean;
}

function computeTransitions(rows: MembershipChangeRow[]): TransitionRow[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const from = asString(row.previous_status);
    const to = asString(row.new_status);
    if (!from || !to || from === to) continue;
    const key = `${from}|||${to}`;
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([key, count]) => {
      const [from, to] = key.split("|||");
      return {
        from,
        to,
        count,
        isNegative: NEGATIVE_NEW_STATUSES.has(to),
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

// ── At-risk member computation ────────────────────────────────────────────────

interface RiskyMember {
  name: string;
  status: string;
  signals: string[];
  score: number;
  suggestedAction: string;
}

function computeAtRisk(rows: AtRiskRow[]): RiskyMember[] {
  const now = new Date();
  return rows
    .map((row) => {
      const signals: string[] = [];

      // Signal 1: Zero 2024 meeting attendance
      const att2024 =
        row.t_2024_co_meeting_attendance ??
        row.meeting_attendance_last_year ??
        null;
      const attYtd = row.meeting_attendance_ytd ?? null;
      if (
        (att2024 === 0 || att2024 === null) &&
        (attYtd === 0 || attYtd === null)
      ) {
        signals.push("No meeting attendance");
      }

      // Signal 2: High inactive_months (>6)
      const inactiveMonths = asNumber(row.inactive_months, 0);
      if (inactiveMonths > 6) {
        signals.push(`${inactiveMonths}mo inactive`);
      }

      // Signal 3: Probationary for >365 days
      const status = asString(row.status_new);
      if (status.toLowerCase().includes("probationary") && row.joined_date) {
        const joinDate = new Date(row.joined_date);
        if (!isNaN(joinDate.getTime())) {
          const daysAsMember =
            (now.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24);
          if (daysAsMember > 365) {
            signals.push(`Probationary ${Math.round(daysAsMember / 30)}mo`);
          }
        }
      }

      const score = signals.length;
      let suggestedAction = "Monitor activity";
      if (signals.some((s) => s.includes("No meeting")))
        suggestedAction = "Reach out — schedule meeting check-in";
      else if (signals.some((s) => s.includes("inactive")))
        suggestedAction = "Contact — review engagement options";
      else if (signals.some((s) => s.includes("Probationary")))
        suggestedAction = "Review probationary status extension";

      return {
        name: asString(row.full_name, "Unknown Member"),
        status,
        signals,
        score,
        suggestedAction,
      };
    })
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score);
}

// ── KPI computations ──────────────────────────────────────────────────────────

function computeKPIs(rows: MembershipChangeRow[], cutoff: string | null) {
  const yearStart = `${new Date().getFullYear()}-01-01`;

  let separationsInPeriod = 0;
  let totalTenureMonths = 0;
  let tenureCount = 0;
  const causeCounts = new Map<string, number>();

  for (const row of rows) {
    const newStatus = asString(row.new_status);
    const effDate = asString(row.effective_date);

    // Count separations within selected period
    const periodStart = cutoff ?? yearStart;
    if (NEGATIVE_NEW_STATUSES.has(newStatus) && effDate >= periodStart) {
      separationsInPeriod++;
    }

    // Attrition cause
    const cause = asString(row.attrition_cause);
    if (cause) {
      causeCounts.set(cause, (causeCounts.get(cause) ?? 0) + 1);
    }

    // Average tenure
    const los = row.los_active_months;
    if (los !== null && los !== undefined) {
      const n = asNumber(los, -1);
      if (n >= 0) {
        totalTenureMonths += n;
        tenureCount++;
      }
    }
  }

  const topCause =
    Array.from(causeCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ??
    "—";
  const avgTenure =
    tenureCount > 0 ? (totalTenureMonths / tenureCount).toFixed(1) : null;

  return { separationsInPeriod, topCause, avgTenure };
}

// ── Retention rate bar ────────────────────────────────────────────────────────

function RetentionRateBar({
  label,
  rate,
  cohortSize,
  isActive,
}: {
  label: string;
  rate: number | null;
  cohortSize: number;
  isActive: boolean;
}) {
  const pct = rate !== null ? Math.round(rate * 100) : null;
  const color =
    pct === null
      ? "#6b7280"
      : pct >= 70
        ? "#16a34a"
        : pct >= 50
          ? "#f59e0b"
          : "#dc2626";

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive ? "bg-theme-overlay" : "hover:bg-theme-overlay/50"}`}
    >
      <span className="text-xs font-medium text-theme-secondary w-20 shrink-0">
        {label}
      </span>
      <div className="flex-1 bg-theme-subtle rounded-full h-2 min-w-0">
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${pct ?? 0}%`, backgroundColor: color }}
        />
      </div>
      <span
        className="text-xs font-bold tabular-nums w-10 text-right"
        style={{ color }}
      >
        {pct !== null ? `${pct}%` : "—"}
      </span>
      {cohortSize > 0 && (
        <span className="text-xs text-theme-muted tabular-nums w-16 text-right shrink-0">
          n={cohortSize}
        </span>
      )}
    </div>
  );
}

// ── Custom Tooltip for bar charts ─────────────────────────────────────────────

function BarTooltip({
  active,
  payload,
  labelKey = "name",
  countLabel = "count",
  totalCount,
}: {
  active?: boolean;
  payload?: Array<{ payload: Record<string, unknown>; value: number }>;
  labelKey?: string;
  countLabel?: string;
  totalCount?: number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const entry = payload[0];
  const pct =
    totalCount && totalCount > 0
      ? ` (${Math.round((entry.value / totalCount) * 100)}%)`
      : "";
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs shadow-lg min-w-[140px]">
      <p className="font-semibold text-theme-primary">
        {String(entry.payload[labelKey] ?? "")}
      </p>
      <p className="text-theme-secondary mt-0.5">
        <span className="font-bold text-brand-adaptive">
          {entry.value.toLocaleString()}
        </span>{" "}
        {countLabel}
        {pct}
      </p>
    </div>
  );
}

// ── Attrition Cause Chart ─────────────────────────────────────────────────────

function AttritionCauseChart({
  causes,
  isLoading,
}: {
  causes: CauseRow[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  const total = causes.reduce((s, c) => s + c.count, 0);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="skeleton h-4 w-36 rounded-md shrink-0" />
            <div
              className="skeleton h-7 rounded-md flex-1"
              style={{ maxWidth: `${80 - i * 12}%` }}
            />
            <div className="skeleton h-4 w-8 rounded-md shrink-0" />
          </div>
        ))}
      </div>
    );
  }

  if (causes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">
          No attrition cause data available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Attrition cause field may not be populated in this dataset
        </p>
      </div>
    );
  }

  const topCause = causes[0];
  const chartData = causes.map((c) => ({
    cause: c.cause,
    count: c.count,
    isTop: c.isTop,
    pct: total > 0 ? Math.round((c.count / total) * 100) : 0,
  }));
  const maxCount = Math.max(...causes.map((c) => c.count), 1);

  return (
    <div className="flex flex-col gap-4">
      {/* Top cause call-out */}
      {topCause && (
        <div className="mx-4 mt-2 flex items-start gap-3 px-4 py-3 rounded-xl bg-red-500/8 border border-red-500/20">
          <div className="w-2 h-2 rounded-full bg-status-red shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-status-red uppercase tracking-wider">
              Top Cause
            </p>
            <p className="text-sm font-bold text-theme-primary mt-0.5">
              {topCause.cause}
            </p>
            <p className="text-xs text-theme-muted mt-0.5">
              {topCause.count} separation{topCause.count !== 1 ? "s" : ""} ·{" "}
              {total > 0 ? Math.round((topCause.count / total) * 100) : 0}% of
              all attrition
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-theme-muted">Call to action</p>
            <p className="text-xs font-semibold text-theme-secondary mt-0.5">
              Review participation requirements
            </p>
          </div>
        </div>
      )}

      <ResponsiveContainer
        width="100%"
        height={Math.max(causes.length * 44 + 20, 180)}
      >
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 0, right: 60, left: 160, bottom: 0 }}
          barCategoryGap="25%"
        >
          <CartesianGrid
            horizontal={false}
            stroke={gridColor}
            strokeDasharray="3 3"
          />
          <XAxis
            type="number"
            domain={[0, maxCount]}
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="cause"
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
            width={155}
          />
          <Tooltip
            content={
              <BarTooltip
                labelKey="cause"
                countLabel="separations"
                totalCount={total}
              />
            }
            cursor={{
              fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
            }}
          />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
            {chartData.map((entry, idx) => (
              <Cell
                key={`cause-${idx}`}
                fill={entry.isTop ? TOP_CAUSE_COLOR : DCVFD_ACCENT}
                fillOpacity={0.85}
              />
            ))}
            <LabelList
              dataKey="pct"
              position="right"
              style={{ fontSize: 11, fontWeight: 600, fill: axisColor }}
              formatter={(v: unknown) =>
                typeof v === "number" ? `${v}%` : String(v ?? "")
              }
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Timing Histogram ──────────────────────────────────────────────────────────

function TimingHistogram({
  buckets,
  isLoading,
}: {
  buckets: TimingBucket[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  const total = buckets.reduce((s, b) => s + b.count, 0);

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 200 }} />;
  }

  const hasData = buckets.some((b) => b.count > 0);
  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">No timing data available</p>
        <p className="text-xs text-theme-muted opacity-60">
          Requires los_active_months field to be populated
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={buckets}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        barCategoryGap="15%"
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={36}
        />
        <Tooltip
          content={
            <BarTooltip
              labelKey="label"
              countLabel="separations"
              totalCount={total}
            />
          }
          cursor={{
            fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          }}
        />
        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={48}>
          {buckets.map((_, idx) => (
            <Cell
              key={`timing-${idx}`}
              fill={idx <= 1 ? TOP_CAUSE_COLOR : DCVFD_ACCENT}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Status Transition Table ───────────────────────────────────────────────────

function TransitionTable({
  rows,
  isLoading,
}: {
  rows: TransitionRow[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              {["Previous Status", "New Status", "Count"].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="border-b border-theme-subtle">
                {[140, 120, 40].map((w, j) => (
                  <td key={j} className="px-4 py-3">
                    <div
                      className="skeleton h-4 rounded-md"
                      style={{ width: w }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-theme-muted text-sm">
        No transition data available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Previous Status
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              New Status
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Count
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={idx}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td className="px-4 py-3 text-sm text-theme-secondary">
                {row.from}
              </td>
              <td className="px-4 py-3 text-sm">
                <span
                  className={`font-medium ${
                    row.isNegative ? "text-status-red" : "text-status-green"
                  }`}
                >
                  {row.to}
                </span>
              </td>
              <td className="px-4 py-3 text-sm font-bold text-theme-primary text-right tabular-nums">
                {row.count.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── At-Risk Alert List ────────────────────────────────────────────────────────

function AtRiskSection({
  members,
  isLoading,
}: {
  members: RiskyMember[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="glass rounded-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between">
          <div className="skeleton h-4 w-32 rounded-md" />
          <div className="skeleton h-5 w-8 rounded-full" />
        </div>
        <div className="p-4 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-14 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-theme-primary">
            At-Risk Members
          </h2>
          <p className="text-xs text-theme-muted mt-0.5">
            Members showing disengagement signals — scored by risk factor count
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {members.filter((m) => m.score >= 3).length > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-status-red">
              {members.filter((m) => m.score >= 3).length} critical
            </span>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted">
            {members.length}
          </span>
        </div>
      </div>

      <div className="p-4">
        {members.length === 0 ? (
          <div className="py-8 text-center flex flex-col items-center gap-1.5">
            <span className="text-xl">✓</span>
            <p className="text-sm font-medium text-status-green">
              No members currently at risk
            </p>
            <p className="text-xs text-theme-muted">
              All active members are meeting engagement requirements
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((m, idx) => {
              const severity: AlertItem["severity"] =
                m.score >= 3 ? "critical" : m.score === 2 ? "warning" : "info";
              const borderColor =
                severity === "critical"
                  ? "border-l-status-red"
                  : severity === "warning"
                    ? "border-l-status-amber"
                    : "border-l-[#3b82f6]";
              const bgColor =
                severity === "critical"
                  ? "bg-red-500/5"
                  : severity === "warning"
                    ? "bg-amber-500/5"
                    : "bg-blue-500/5";
              const dotColor =
                severity === "critical"
                  ? "bg-status-red"
                  : severity === "warning"
                    ? "bg-status-amber"
                    : "bg-blue-500";

              return (
                <div
                  key={`${m.name}-${idx}`}
                  className={`flex items-start gap-3 px-3 py-3 border-l-4 rounded-r-lg ${borderColor} ${bgColor}`}
                >
                  <div className="mt-1 shrink-0">
                    <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-theme-primary leading-snug">
                        {m.name}
                      </p>
                      <span className="text-xs text-theme-muted">
                        {m.status}
                      </span>
                    </div>
                    <p className="text-xs text-theme-muted mt-0.5">
                      {m.signals.join(" · ")}
                    </p>
                    <p className="text-xs font-medium text-brand-adaptive mt-1">
                      {m.suggestedAction}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded-md ${
                      severity === "critical"
                        ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        : severity === "warning"
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    }`}
                  >
                    {m.score} signal{m.score !== 1 ? "s" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Separation History DataTable ──────────────────────────────────────────────

type SepSortField =
  | "name"
  | "date"
  | "prevStatus"
  | "newStatus"
  | "cause"
  | "tenure";
type SortDir = "asc" | "desc";

function SepSortIcon({
  field,
  sortField,
  sortDir,
}: {
  field: SepSortField;
  sortField: SepSortField;
  sortDir: SortDir;
}) {
  if (field !== sortField) {
    return (
      <svg
        className="inline ml-1 opacity-30"
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    );
  }
  return sortDir === "asc" ? (
    <svg
      className="inline ml-1 opacity-80 text-brand-adaptive"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ) : (
    <svg
      className="inline ml-1 opacity-80 text-brand-adaptive"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function SeparationDataTable({
  rows,
  isLoading,
}: {
  rows: MembershipChangeRow[];
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [causeFilter, setCauseFilter] = useState("all");
  const [sortField, setSortField] = useState<SepSortField>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  // Only show negative transitions (separations)
  const separations = useMemo(
    () => rows.filter((r) => NEGATIVE_NEW_STATUSES.has(asString(r.new_status))),
    [rows],
  );

  const causes = useMemo(() => {
    const seen = new Set<string>();
    for (const r of separations) {
      const c = asString(r.attrition_cause);
      if (c) seen.add(c);
    }
    return Array.from(seen).sort();
  }, [separations]);

  const filtered = useMemo(() => {
    let result = separations;
    if (causeFilter !== "all") {
      result = result.filter(
        (r) => asString(r.attrition_cause) === causeFilter,
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          asString(r.full_name_co_id).toLowerCase().includes(q) ||
          asString(r.new_status).toLowerCase().includes(q) ||
          asString(r.attrition_cause).toLowerCase().includes(q),
      );
    }
    return result;
  }, [separations, causeFilter, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va: string | number = "";
      let vb: string | number = "";
      switch (sortField) {
        case "name":
          va = asString(a.full_name_co_id);
          vb = asString(b.full_name_co_id);
          break;
        case "date":
          va = asString(a.effective_date);
          vb = asString(b.effective_date);
          break;
        case "prevStatus":
          va = asString(a.previous_status);
          vb = asString(b.previous_status);
          break;
        case "newStatus":
          va = asString(a.new_status);
          vb = asString(b.new_status);
          break;
        case "cause":
          va = asString(a.attrition_cause);
          vb = asString(b.attrition_cause);
          break;
        case "tenure":
          va = asNumber(a.los_active_months);
          vb = asNumber(b.los_active_months);
          break;
      }
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [filtered, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function toggleSort(field: SepSortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(0);
  }

  const thClass =
    "px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider cursor-pointer select-none hover:text-theme-primary transition-colors whitespace-nowrap";

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <div className="flex gap-3 mb-4">
          <div className="skeleton h-9 w-64 rounded-lg" />
          <div className="skeleton h-9 w-40 rounded-lg" />
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-lg" />
        ))}
      </div>
    );
  }

  if (separations.length === 0) {
    return (
      <div className="py-12 text-center flex flex-col items-center gap-2">
        <p className="text-sm text-theme-muted">
          No separation records available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Data will appear after the next membership sync
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Filter bar */}
      <div className="flex items-center gap-3 px-4 pt-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-muted"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name or status…"
            className="w-full pl-8 pr-3 py-2 text-sm bg-theme-overlay border border-theme-subtle rounded-lg text-theme-primary placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
          />
        </div>

        <select
          value={causeFilter}
          onChange={(e) => {
            setCauseFilter(e.target.value);
            setPage(0);
          }}
          className="px-3 py-2 text-sm bg-theme-overlay border border-theme-subtle rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
        >
          <option value="all">All Causes</option>
          {causes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <span className="ml-auto text-xs text-theme-muted tabular-nums shrink-0">
          {filtered.length.toLocaleString()} of{" "}
          {separations.length.toLocaleString()} separations
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              <th className={thClass} onClick={() => toggleSort("name")}>
                Name{" "}
                <SepSortIcon
                  field="name"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("date")}>
                Date{" "}
                <SepSortIcon
                  field="date"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("prevStatus")}>
                Previous Status{" "}
                <SepSortIcon
                  field="prevStatus"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("newStatus")}>
                New Status{" "}
                <SepSortIcon
                  field="newStatus"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("cause")}>
                Cause{" "}
                <SepSortIcon
                  field="cause"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th
                className={`${thClass} text-right`}
                onClick={() => toggleSort("tenure")}
              >
                Tenure{" "}
                <SepSortIcon
                  field="tenure"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center text-sm text-theme-muted"
                >
                  No records match your filters
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => {
                const name = asString(row.full_name_co_id);
                const tenure = asNumber(row.los_active_months, -1);
                return (
                  <tr
                    key={idx}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-sm font-semibold text-theme-primary whitespace-nowrap">
                      {name || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-theme-muted tabular-nums whitespace-nowrap">
                      {formatDate(row.effective_date)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-theme-secondary">
                      {asString(row.previous_status) || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-status-red">
                      {asString(row.new_status) || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-theme-secondary">
                      {asString(row.attrition_cause) || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-right tabular-nums text-theme-secondary">
                      {tenure >= 0 ? `${tenure} mo` : "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 pb-4 gap-3">
          <span className="text-xs text-theme-muted tabular-nums">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-xs rounded-lg bg-theme-overlay text-theme-secondary disabled:opacity-30 hover:bg-theme-overlay/80 transition-colors"
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
              const pageNum =
                totalPages <= 7
                  ? i
                  : Math.max(0, Math.min(totalPages - 7, page - 3)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-7 h-7 text-xs rounded-lg transition-colors ${
                    page === pageNum
                      ? "bg-dcvfd-accent text-white font-bold"
                      : "bg-theme-overlay text-theme-secondary hover:bg-theme-overlay/80"
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-xs rounded-lg bg-theme-overlay text-theme-secondary disabled:opacity-30 hover:bg-theme-overlay/80 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 2024 Monthly Attrition Chart ──────────────────────────────────────────────

function AttritionMonthlyChart({
  rows,
  isLoading,
}: {
  rows: AttritionMonthlyRow[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 240 }} />;
  }

  // Only include months that have data for year 2024
  const chartData = rows
    .filter(
      (r) =>
        r.year === 2024 &&
        r.month &&
        (r.additions !== null || r.separations !== null),
    )
    .map((r) => ({
      month: String(r.month ?? ""),
      additions: r.additions ?? 0,
      separations: r.separations ?? 0,
      attrition_pct: r.attrition_pct ?? 0,
      overRatio: (r.attrition_pct ?? 0) > 1,
    }));

  if (chartData.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-10">
        No 2024 attrition data available
      </p>
    );
  }

  const totalAdditions = chartData.reduce((s, r) => s + r.additions, 0);
  const totalSeparations = chartData.reduce((s, r) => s + r.separations, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 px-1 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-theme-secondary">
          <span
            className="w-3 h-3 rounded-sm shrink-0"
            style={{ background: DCVFD_ACCENT, opacity: 0.85 }}
          />
          <span>Additions ({totalAdditions})</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-theme-secondary">
          <span
            className="w-3 h-3 rounded-sm shrink-0"
            style={{ background: "#dc2626", opacity: 0.85 }}
          />
          <span>Separations ({totalSeparations})</span>
        </div>
        {totalSeparations > totalAdditions && (
          <span className="text-xs font-semibold text-status-red ml-auto">
            Net: −{totalSeparations - totalAdditions} members in 2024
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          barCategoryGap="20%"
        >
          <CartesianGrid
            vertical={false}
            stroke={gridColor}
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 10, fill: axisColor }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: string) => v.slice(0, 3)}
          />
          <YAxis
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
            width={30}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: getChartTheme(isDark).tooltipBg,
              border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(value, name) => [
              value,
              name === "additions" ? "Additions" : "Separations",
            ]}
          />
          <Bar
            dataKey="additions"
            fill={DCVFD_ACCENT}
            fillOpacity={0.85}
            radius={[3, 3, 0, 0]}
            maxBarSize={28}
          />
          <Bar dataKey="separations" radius={[3, 3, 0, 0]} maxBarSize={28}>
            {chartData.map((d, idx) => (
              <Cell
                key={idx}
                fill="#dc2626"
                fillOpacity={d.overRatio ? 1 : 0.7}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Survey Discontinue Drivers Chart ─────────────────────────────────────────

function SurveyDiscontinueChart({
  themes,
  isLoading,
}: {
  themes: RetentionThemeRow[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  const data = useMemo(() => {
    return themes
      .filter((t) => t.theme_table === "discontinue_reasons")
      .map((t) => ({
        label: t.theme_label ?? "",
        value: (() => {
          const v = t.theme_value ?? "0";
          const n = parseFloat(v);
          return Number.isFinite(n) ? n : 0;
        })(),
      }))
      .sort((a, b) => b.value - a.value);
  }, [themes]);

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 200 }} />;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-8">
        No survey data available yet
      </p>
    );
  }

  const height = Math.max(180, data.length * 44);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          horizontal={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={210}
          tickFormatter={(v: string) =>
            v.length > 28 ? v.slice(0, 26) + "…" : v
          }
        />
        <Tooltip
          contentStyle={{
            background: isDark ? "#1c1c1e" : "#fff",
            border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value) => [`${value} mentions`, "Reason"]}
          labelStyle={{ fontWeight: 600 }}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
          {data.map((_, idx) => (
            <Cell
              key={idx}
              fill={idx === 0 ? TOP_CAUSE_COLOR : DCVFD_ACCENT}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RetentionAnalysis() {
  const [dateRange, setDateRange] = useState<DateRange>("all");

  // ── Data fetching ─────────────────────────────────────────────────────────
  const {
    data: aggData,
    isLoading: _aggLoading,
    lastUpdatedAt: aggUpdated,
  } = useAggregates();
  const { data: changesData, isLoading: changesLoading } =
    useMembershipChanges();
  const { data: cohortsData, isLoading: cohortsLoading } =
    useRetentionCohorts();
  const { data: atRiskData, isLoading: atRiskLoading } = useAtRisk();
  const { data: benchmarksData, isLoading: benchmarksLoading } =
    useBenchmarks(300_000);
  // Static source data
  const { data: themesData, isLoading: themesLoading } = useRetentionThemes();
  const { data: attritionMonthlyData, isLoading: attritionMonthlyLoading } =
    useAttritionMonthly();

  const agg = aggData?.aggregates ?? {};
  const changes: MembershipChangeRow[] = changesData?.data ?? [];
  const cohortRows: RetentionCohortRow[] = cohortsData?.data ?? [];
  const atRiskRows: AtRiskRow[] = atRiskData?.data ?? [];
  const surveyThemes: RetentionThemeRow[] = themesData?.data ?? [];
  const attritionMonthly: AttritionMonthlyRow[] =
    attritionMonthlyData?.data ?? [];

  // ── Date range cutoff ─────────────────────────────────────────────────────
  const cutoff = getDateRangeCutoff(dateRange);

  // ── Filter changes by date range ──────────────────────────────────────────
  const filteredChanges = useMemo<MembershipChangeRow[]>(() => {
    if (!cutoff) return changes;
    return changes.filter((r) => {
      const d = asString(r.effective_date);
      return !d || d >= cutoff;
    });
  }, [changes, cutoff]);

  // ── Retention rates (multi-period) ────────────────────────────────────────
  const retentionPeriods = useMemo<RetentionPeriod[]>(() => {
    if (cohortRows.length === 0) {
      return [
        { label: "YTD", years: 0, rate: null, cohortSize: 0 },
        { label: "1-Year", years: 1, rate: null, cohortSize: 0 },
        { label: "3-Year", years: 3, rate: null, cohortSize: 0 },
        { label: "5-Year", years: 5, rate: null, cohortSize: 0 },
      ];
    }

    const ytd = computeYtdRetention(cohortRows);
    const y1 = computeRetentionRate(cohortRows, 1);
    const y3 = computeRetentionRate(cohortRows, 3);
    const y5 = computeRetentionRate(cohortRows, 5);

    return [
      { label: "YTD", years: 0, rate: ytd.rate, cohortSize: ytd.cohortSize },
      { label: "1-Year", years: 1, rate: y1.rate, cohortSize: y1.cohortSize },
      { label: "3-Year", years: 3, rate: y3.rate, cohortSize: y3.cohortSize },
      { label: "5-Year", years: 5, rate: y5.rate, cohortSize: y5.cohortSize },
    ];
  }, [cohortRows]);

  // Gauge shows the most relevant period based on selected date range
  const activeGaugePeriod = useMemo<RetentionPeriod>(() => {
    if (dateRange === "ytd") return retentionPeriods[0];
    if (dateRange === "l12m") return retentionPeriods[1];
    if (dateRange === "l3y") return retentionPeriods[2];
    if (dateRange === "l5y") return retentionPeriods[3];
    // All time — show 3-year as most useful
    return retentionPeriods[2] ?? retentionPeriods[1] ?? retentionPeriods[0];
  }, [dateRange, retentionPeriods]);

  // Legacy aggregate for fallback — normalize to 0-1 fraction regardless of storage format
  const firstYearRetentionRateRaw = asNumber(agg["first_year_retention_rate"]);
  // If stored as percentage (e.g. 38.5 instead of 0.385), divide by 100
  const firstYearRetentionRate =
    firstYearRetentionRateRaw > 1
      ? firstYearRetentionRateRaw / 100
      : firstYearRetentionRateRaw;

  // ── Cohort survival curves ────────────────────────────────────────────────
  const cohortSeries = useMemo<CohortSeries[]>(() => {
    if (cohortRows.length === 0) return [];
    return computeCohortSeries(cohortRows);
  }, [cohortRows]);

  // Compare most recent vs older cohorts for trend callout
  const recentCohortBetter = useMemo(() => {
    if (cohortSeries.length < 2) return null;
    const recent = cohortSeries[cohortSeries.length - 1];
    const older = cohortSeries[cohortSeries.length - 2];
    const recentPct =
      recent.dataPoints[recent.dataPoints.length - 1]?.pctActive ?? 0;
    const olderPct =
      older.dataPoints[older.dataPoints.length - 1]?.pctActive ?? 0;
    if (Math.abs(recentPct - olderPct) < 3) return null;
    return {
      better: recentPct > olderPct,
      diff: Math.abs(recentPct - olderPct).toFixed(1),
      recentYear: recent.year,
    };
  }, [cohortSeries]);

  // ── Attrition causes ──────────────────────────────────────────────────────
  const attritionCauses = useMemo<CauseRow[]>(() => {
    if (filteredChanges.length === 0) return [];
    return computeAttritionCauses(filteredChanges);
  }, [filteredChanges]);

  // ── Attrition timing ──────────────────────────────────────────────────────
  const timingBuckets = useMemo<TimingBucket[]>(() => {
    if (filteredChanges.length === 0)
      return TIMING_BUCKETS.map((b) => ({ label: b.label, count: 0 }));
    return computeAttritionTiming(filteredChanges);
  }, [filteredChanges]);

  // ── At-risk members ───────────────────────────────────────────────────────
  const riskyMembers = useMemo<RiskyMember[]>(() => {
    if (atRiskRows.length === 0) return [];
    return computeAtRisk(atRiskRows);
  }, [atRiskRows]);

  // ── Status transitions ────────────────────────────────────────────────────
  const transitions = useMemo<TransitionRow[]>(() => {
    if (filteredChanges.length === 0) return [];
    return computeTransitions(filteredChanges);
  }, [filteredChanges]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const { separationsInPeriod, topCause, avgTenure } = useMemo(
    () => computeKPIs(changes, cutoff),
    [changes, cutoff],
  );

  // Refresh timestamp
  const sec = aggUpdated
    ? Math.round((Date.now() - aggUpdated.getTime()) / 1000)
    : null;

  // Gauge value: prefer computed cohort rate, fall back to aggregate
  const gaugeValue = activeGaugePeriod.rate ?? firstYearRetentionRate;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Retention &amp; Attrition Analysis
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Why members leave, when they leave, and who is at risk.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="FORGE data" variant="blue" />
          {sec !== null && (
            <div className="flex items-center gap-1.5 text-xs text-theme-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
              <span>Updated {sec < 5 ? "just now" : `${sec}s ago`}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Date range toggle ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-medium text-theme-muted uppercase tracking-wider shrink-0">
          Period:
        </span>
        <DateRangeToggle value={dateRange} onChange={setDateRange} />
      </div>

      {/* ── Section 1: Top row — Large Gauge + multi-period + KPI cards ─ */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {/* Large gauge + multi-period retention bars */}
          <ChartCard
            title="Retention Rates"
            subtitle={`${activeGaugePeriod.label} rate — target 55%`}
            minHeight={0}
          >
            {cohortsLoading ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="skeleton h-4 w-32 rounded-md" />
                <div
                  className="skeleton rounded-full"
                  style={{ width: 160, height: 80 }}
                />
                <div className="skeleton h-8 w-20 rounded-md" />
              </div>
            ) : (
              <>
                {/* Prominent gauge */}
                <GaugeChart
                  value={gaugeValue}
                  target={0.55}
                  label={`${activeGaugePeriod.label} Retention`}
                />
                {/* National benchmark indicator */}
                {(() => {
                  const retBench = benchmarksData?.benchmarks?.find(
                    (b) => b.metric === "first_year_retention",
                  );
                  const current = retBench?.currentValue ?? gaugeValue;
                  if (!retBench || current === null) return null;
                  return (
                    <div className="mt-3 px-1">
                      <BenchmarkIndicator
                        current={current}
                        nationalAverage={retBench.nationalAverage}
                        topQuartile={retBench.topQuartile}
                        unit="percent"
                        direction="higher-better"
                        label="vs. National Average"
                        source={retBench.source}
                        note={retBench.note}
                        isLoading={benchmarksLoading}
                      />
                    </div>
                  );
                })()}
                {/* Multi-window breakdown */}
                <div className="mt-4 space-y-0.5">
                  <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider px-3 mb-1">
                    All Windows
                  </p>
                  {retentionPeriods.map((p) => (
                    <RetentionRateBar
                      key={p.label}
                      label={p.label}
                      rate={p.rate}
                      cohortSize={p.cohortSize}
                      isActive={p.label === activeGaugePeriod.label}
                    />
                  ))}
                </div>
              </>
            )}
          </ChartCard>

          {/* KPI cards — right 2/3 */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KPICard
              label={`Separations (${DATE_RANGE_LABELS[dateRange]})`}
              value={changesLoading ? "—" : separationsInPeriod}
              subtitle="members separated in period"
              accent={
                separationsInPeriod > 10
                  ? "red"
                  : separationsInPeriod > 5
                    ? "amber"
                    : "neutral"
              }
              isLoading={changesLoading}
            />
            <KPICard
              label="Top Attrition Cause"
              value={
                changesLoading
                  ? "—"
                  : topCause.length > 18
                    ? topCause.slice(0, 16) + "…"
                    : topCause
              }
              subtitle="most common separation reason"
              accent="neutral"
              isLoading={changesLoading}
            />
            <KPICard
              label="Avg Tenure at Sep."
              value={
                changesLoading || avgTenure === null ? "—" : `${avgTenure} mo`
              }
              subtitle="months active before leaving"
              accent="neutral"
              isLoading={changesLoading}
            />

            {/* At-risk members KPI */}
            <KPICard
              label="At-Risk Members"
              value={atRiskLoading ? "—" : riskyMembers.length}
              subtitle={
                riskyMembers.filter((m) => m.score >= 3).length > 0
                  ? `${riskyMembers.filter((m) => m.score >= 3).length} critical — immediate action needed`
                  : riskyMembers.length === 0
                    ? "All members engaged"
                    : "Monitor and follow up"
              }
              accent={
                riskyMembers.filter((m) => m.score >= 3).length > 0
                  ? "red"
                  : riskyMembers.length > 0
                    ? "amber"
                    : "green"
              }
              isLoading={atRiskLoading}
            />
            <KPICard
              label="Cohort Years"
              value={cohortsLoading ? "—" : cohortSeries.length}
              subtitle={
                recentCohortBetter !== null
                  ? recentCohortBetter.better
                    ? `${recentCohortBetter.recentYear} cohort +${recentCohortBetter.diff}pp better`
                    : `${recentCohortBetter.recentYear} cohort −${recentCohortBetter.diff}pp worse`
                  : "Join-year cohort curves"
              }
              accent={
                recentCohortBetter === null
                  ? "neutral"
                  : recentCohortBetter.better
                    ? "green"
                    : "amber"
              }
              isLoading={cohortsLoading}
            />
            <KPICard
              label="Peak Attrition Window"
              value={
                changesLoading
                  ? "—"
                  : (() => {
                      const peak = timingBuckets.reduce(
                        (best, b) => (b.count > best.count ? b : best),
                        timingBuckets[0],
                      );
                      return peak && peak.count > 0 ? peak.label : "—";
                    })()
              }
              subtitle={(() => {
                const peak = timingBuckets.reduce(
                  (best, b) => (b.count > best.count ? b : best),
                  timingBuckets[0],
                );
                const total = timingBuckets.reduce((s, b) => s + b.count, 0);
                if (!peak || peak.count === 0 || total === 0)
                  return "when most members leave";
                return `${Math.round((peak.count / total) * 100)}% of separations — target early intervention`;
              })()}
              accent="neutral"
              isLoading={changesLoading}
            />
          </div>
        </div>
      </section>

      {/* ── Section 2: Cohort Survival Curves ──────────────────────── */}
      <section>
        <ChartCard
          title="Cohort Survival Curves"
          subtitle="% of each join-year cohort still active at 0, 3, 6, 12, 24, 36, and 60 months"
          headerRight={
            cohortsLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : cohortSeries.length > 0 ? (
              <div className="flex items-center gap-2">
                {recentCohortBetter !== null && (
                  <span
                    className={`text-xs font-semibold ${recentCohortBetter.better ? "text-status-green" : "text-status-amber"}`}
                  >
                    {recentCohortBetter.better ? "▲" : "▼"} Recent cohort{" "}
                    {recentCohortBetter.better
                      ? "outperforming"
                      : "underperforming"}
                  </span>
                )}
                <StatusBadge
                  label={`${cohortSeries.length} cohorts`}
                  variant="muted"
                />
              </div>
            ) : null
          }
          minHeight={280}
        >
          <CohortChart cohorts={cohortSeries} isLoading={cohortsLoading} />
          {!cohortsLoading && cohortSeries.length > 0 && (
            <p className="text-xs text-theme-muted mt-3 leading-relaxed border-t border-theme-subtle pt-3">
              <span className="font-semibold text-theme-secondary">
                How to read this:
              </span>{" "}
              The dashed{" "}
              <span style={{ color: DCVFD_ACCENT }} className="font-semibold">
                Target 50%
              </span>{" "}
              line is the playbook goal for 12-month retention (current ≈33%).
              Cohorts tagged <span className="font-medium">(in progress)</span>{" "}
              are right-censored — they have not yet had a full 12 months to be
              observed, so their later milestones overstate retention and should
              not be read as a final result. The headline first-year retention
              number reported to the board uses a fixed 12-months-ago join
              window (a tighter cut than any single calendar-year cohort shown
              here), so it will differ from a given join-year's curve.
            </p>
          )}
        </ChartCard>
      </section>

      {/* ── Section 3: Attrition Cause Breakdown ───────────────────── */}
      <section>
        <ChartCard
          title="Attrition Cause Breakdown"
          subtitle="Why members leave — top cause highlighted with call-to-action, % of total shown"
          headerRight={
            changesLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : attritionCauses.length > 0 ? (
              <StatusBadge
                label={`${attritionCauses.length} causes`}
                variant="muted"
              />
            ) : null
          }
          minHeight={
            attritionCauses.length > 0
              ? Math.max(attritionCauses.length * 44 + 80, 220)
              : 160
          }
        >
          <AttritionCauseChart
            causes={attritionCauses}
            isLoading={changesLoading}
          />
        </ChartCard>
      </section>

      {/* ── Section 4: Attrition Timing Histogram ──────────────────── */}
      <section>
        <ChartCard
          title="Attrition Timing Distribution"
          subtitle='When members leave — the "cliff" typically shows up in the first 6–12 months'
          minHeight={220}
        >
          <TimingHistogram buckets={timingBuckets} isLoading={changesLoading} />
        </ChartCard>
      </section>

      {/* ── Section 5: At-Risk Members ─────────────────────────────── */}
      <section>
        <AtRiskSection members={riskyMembers} isLoading={atRiskLoading} />
      </section>

      {/* ── Section 6: Status Transition Summary ───────────────────── */}
      <section>
        <ChartCard
          title="Status Transition Summary"
          subtitle="Top 10 most common membership status changes — negative transitions highlighted in red"
          minHeight={0}
        >
          <TransitionTable rows={transitions} isLoading={changesLoading} />
        </ChartCard>
      </section>

      {/* ── Section 7: Separation History DataTable ────────────────── */}
      <section>
        <ChartCard
          title="Separation History"
          subtitle="All membership separations — sortable by date, status, cause, or tenure; filterable and searchable"
          headerRight={
            changesLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : (
              <StatusBadge
                label={`${changes.filter((r) => NEGATIVE_NEW_STATUSES.has(asString(r.new_status))).length} separations`}
                variant="muted"
              />
            )
          }
          minHeight={0}
        >
          <SeparationDataTable rows={changes} isLoading={changesLoading} />
        </ChartCard>
      </section>

      {/* ── Section 8: 2024 Monthly Attrition Ledger ──────────────── */}
      <section>
        <ChartCard
          title="2024 Monthly Attrition Ledger"
          subtitle="83 separations vs 70 additions — months with attrition ratio > 1 highlighted red"
          minHeight={220}
        >
          <AttritionMonthlyChart
            rows={attritionMonthly}
            isLoading={attritionMonthlyLoading}
          />
        </ChartCard>
      </section>

      {/* ── AI Risk Monitor (admin only) ──────────────────────────── */}
      <section>
        <RiskMonitor />
      </section>

      {/* ── Section 9: Member Voice Survey (Dec 2024) ─────────────── */}
      <section>
        <ChartCard
          title="Member Voice: Retention Survey (Dec 2024)"
          subtitle="Top discontinuation drivers from 60 survey responses · Full analysis on Staffing Coverage page"
          headerRight={
            <Link
              to="/staffing"
              className="text-xs font-medium text-brand-adaptive hover:underline"
            >
              Full survey detail →
            </Link>
          }
          minHeight={0}
        >
          <SurveyDiscontinueChart
            themes={surveyThemes}
            isLoading={themesLoading}
          />
        </ChartCard>
      </section>
    </div>
  );
}
